import React from "react";

import { useTranslation } from "utils";

import { Button, Drawer, Icon, Typography } from "components";

import { openSoilSightFeedbackForm } from "pages/app/scenario-analysis/components/BetaNotice";

const BETA_LISTS = [
  {
    group: "goodFor",
    modifier: "good-for",
    iconName: "tabler/circle-check",
    itemKeys: ["ranking", "coverCropFit", "fieldsGainMost", "soilWater", "noTillTransition"],
  },
  {
    group: "notYet",
    modifier: "not-yet",
    iconName: "tabler/alert-triangle",
    itemKeys: ["margins", "fertiliser", "weather", "nitrate", "outsideGermany"],
  },
] as const;

export type BetaDrawerProps = {
  visible: boolean;
  onClose: () => void;
  dataTestId?: string;
};

export const BetaDrawer = ({ visible, onClose, dataTestId = "soil-sight-beta-drawer" }: BetaDrawerProps) => {
  const { t } = useTranslation("scenarioAnalysis");

  return (
    // We use the how-this-works drawer classes, so both explainer drawers look the same.
    <Drawer
      className="soil-sight-how-this-works-drawer"
      visible={visible}
      onClose={onClose}
      title={t("betaNotice.drawer.title")}
      hideCloseIcon
      dataTestId={dataTestId}
    >
      <div className="soil-sight-how-this-works">
        <header className="soil-sight-how-this-works-hero">
          <div className="soil-sight-how-this-works-hero-top">
            <span className="soil-sight-how-this-works-hero-badge">
              <Icon iconName="tabler/info-circle" size="m-light" aria-hidden />
            </span>
            <button
              type="button"
              className="soil-sight-how-this-works-close"
              onClick={onClose}
              aria-label={t("global:close")}
              data-testid={`${dataTestId}-close`}
            >
              <Icon iconName="tabler/x" size="m-light" aria-hidden />
            </button>
          </div>
          <Typography variant="h3" color="white" className="soil-sight-how-this-works-title">
            {t("betaNotice.drawer.title")}
          </Typography>
          <Typography variant="paragraph-m" className="soil-sight-how-this-works-intro-text">
            {t("betaNotice.drawer.intro")}
          </Typography>
        </header>

        <div className="soil-sight-how-this-works-body">
          <div className="soil-sight-how-this-works-closing">
            <Typography variant="paragraph-m" bold>
              {t("betaNotice.drawer.note")}
            </Typography>
          </div>

          {BETA_LISTS.map(({ group, modifier, iconName, itemKeys }) => (
            <section key={group} className="soil-sight-how-this-works-section">
              <Typography variant="paragraph-s" className="soil-sight-how-this-works-section-heading">
                {t(`betaNotice.drawer.${group}.heading`)}
              </Typography>
              <ul className={`soil-sight-beta-list soil-sight-beta-list--${modifier}`} data-testid={`${dataTestId}-${modifier}`}>
                {itemKeys.map((itemKey) => (
                  <li key={itemKey} className="soil-sight-beta-list-item">
                    <Icon iconName={iconName} size="m-light" className="soil-sight-beta-list-icon" aria-hidden />
                    <div className="soil-sight-beta-list-copy">
                      <Typography variant="paragraph-m" bold>
                        {t(`betaNotice.drawer.${group}.items.${itemKey}.title`)}
                      </Typography>
                      <Typography variant="paragraph-m" color="grey-600">
                        {t(`betaNotice.drawer.${group}.items.${itemKey}.body`)}
                      </Typography>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          <Button
            type="link"
            onClick={openSoilSightFeedbackForm}
            icon={<Icon iconName="tabler/arrow-right" size="s" />}
            mixpanelText="SoilSight beta: drawer feedback"
            dataTestId={`${dataTestId}-feedback`}
          >
            {t("betaNotice.drawer.feedback")}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default BetaDrawer;
